<template>
  <ViewContainer :title="$t('boards.create_board')">
    <v-container fill-height class="mb-4 px-4 pb-6">
      <v-row class="my-0">
        <v-col cols="8" offset="2">
          <v-btn
            color="primary"
            plain
            text
            :ripple="false"
            @click="handleBackAllBoards"
          >
            <v-icon size="16" class="me-2">
              mdi-arrow-left
            </v-icon>
            {{ $t('boards.all_boards') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="mb-8">
        <v-col offset="2" cols="8">
          <Stepper
            class="my-2"
            hasCompleteError
            :steps="STEPS"
            :phases="phases"
            :activeStep="currentStep"
            @click="handleStepperClick"
          />

          <FormStep
            data-tourid="boards-createBoard-firstStep"
            v-show="currentStep === STEPS.BOARD"
            @continue="handleContinue"
            @cancel="handleCancelCreation"
            @formIsValid="formStepIsValid = $event"
            @change="updateFormField"
          />
          <AddColumnsStep
            data-tourid="boards-createBoard-secondStep"
            v-show="currentStep === STEPS.COLUMNS"
            @back="handleBack"
            @change="handleColumnsChange"
            @continue="handleContinue"
            @cancel="handleCancelCreation"
            @formIsValid="columnStepIsValid = $event"
          />
          <ConfirmationStep
            data-tourid="boards-createBoard-confirmationStep"
            v-show="currentStep === STEPS.CONFIRMATION"
            :disableCreateBoard="disableCreateBoard"
            :board="board"
            :columns="columns"
            :loading="loading"
            @back="handleBack"
            @save="handleSave"
            @cancel="handleCancelCreation"
          />
        </v-col>
      </v-row>
    </v-container>

    <ConfirmationModal
      confirmColor="danger"
      :maxWidth="380"
      :title="$t('boards.cancel_board')"
      :text="$t('boards.cancel_board_text')"
      :confirmText="$t('common.button.yes_cancel')"
      :cancelText="$t('common.button.no_continue')"
      :show="showCancelModal"
      @cancel="handleCancelNavigation"
      @confirm="handleConfirmNavigation"
    />
  </ViewContainer>
</template>

<script>
import _ from 'lodash'
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import ConfirmationModal from '@/components/Modals/ConfirmationModal.vue'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import FormStep from './Steps/FormStep.vue'
import AddColumnsStep from './Steps/AddColumnsStep.vue'
import ConfirmationStep from './Steps/ConfirmationStep.vue'
import Stepper from '@/components/Stepper.vue'

import api from '@/api'
import { mapGetters } from 'vuex'

const STEPS = {
  BOARD: 1,
  COLUMNS: 2,
  CONFIRMATION: 3,
}

export default {
  name: 'Boards',

  components: {
    ViewContainer,
    ConfirmationModal,
    FormStep,
    AddColumnsStep,
    ConfirmationStep,
    Stepper,
  },

  mixins: [mixin, trackerMixin],

  data() {
    return {
      loading: false,
      STEPS,
      currentStep: STEPS.BOARD,
      board: { name: null, description: '' },
      columns: [],
      showCancelModal: false,
      formStepIsValid: true,
      columnStepIsValid: true,
      columnsStepIsDisabled: true,
      confirmationStepIsDisabled: true,
    }
  },

  mounted() {
    if (!this.organization.featuresEnabled__boards) {
      return this.$router.push({ name: 'error404' })
    }
  },

  watch: {
    currentStep: {
      handler(val) {
        if (val === STEPS.COLUMNS) {
          this.columnsStepIsDisabled = false
        }
        if (val === STEPS.CONFIRMATION) {
          this.confirmationStepIsDisabled = false
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapGetters(['organization', 'organizationId', 'accountId']),

    isNameStepCompleted() {
      return Boolean(this.board?.name)
    },

    columnsStepIsCompleted() {
      if (_.isEmpty(this.columns)) return false
      const allColumnsHaveName = this.columns?.every(item => Boolean(item.name))
      const allColumnsHaveFilter = this.columns?.every(
        item => !_.isEmpty(item.filter) || item?.userFilter?.id
      )
      return allColumnsHaveName && allColumnsHaveFilter
    },

    disableCreateBoard() {
      return (
        !this.isNameStepCompleted ||
        !this.columnsStepIsCompleted ||
        !this.columnStepIsValid
      )
    },

    phases() {
      return [
        {
          id: STEPS.BOARD,
          name: this.$t('common.name'),
          complete: this.isNameStepCompleted,
          missingFields: !this.formStepIsValid,
        },
        {
          id: STEPS.COLUMNS,
          name: this.$t('boards.column_other'),
          complete: this.columnsStepIsCompleted,
          disabled: this.columnsStepIsDisabled,
          missingFields: !this.columnStepIsValid,
        },
        {
          id: STEPS.CONFIRMATION,
          name: this.$t('routines.confirmation'),
          complete: this.currentStep > STEPS.CONFIRMATION,
          disabled: this.confirmationStepIsDisabled,
        },
      ]
    },
  },

  methods: {
    handleContinue() {
      this.currentStep = this.currentStep + 1
      this.trackBoardsActions('boards.ContinueCreationBoard')
    },

    handleBack() {
      this.currentStep = this.currentStep - 1
      this.trackBoardsActions('boards.BackCreationBoard')
    },

    handleColumnsChange(columns) {
      this.columns = [...columns]
    },

    async handleSave() {
      try {
        this.loading = true
        const board = await this.createBoard()
        await this.createColumns(board.id)
        this.showSuccess(this.$t('boards.board_created_successfully'))
        this.navigateTo({ name: 'board', params: { boardId: board.id } })
        this.trackBoardsActions('boards.CreateBoard', {
          name: board.name,
          nameLength: board.name?.length,
          description: board.description,
          descriptionLength: board.description?.length,
          columns: this.columns,
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async createBoard() {
      const response = await api.board.create({
        organizationId: this.organizationId,
        data: this.board,
      })
      return response.data
    },

    async createColumns(boardId) {
      const response = await api.board.createColumnBulk({
        organizationId: this.organizationId,
        boardId,
        columns: this.columns.map(column => ({
          ...column,
          userFilter: column?.userFilter?.id,
        })),
      })
      return response.data
    },

    handleBackAllBoards() {
      this.showCancelModal = true
      this.trackBoardsActions('boards.Navigate', {
        section: 'all boards',
      })
    },

    handleCancelNavigation() {
      this.showCancelModal = false
      this.trackBoardsActions('boards.CancelAction', {
        action: 'navigation',
      })
    },

    handleConfirmNavigation() {
      this.navigateTo({ name: 'boards' })
      this.trackBoardsActions('boards.ConfirmAction', {
        action: 'navigation',
      })
    },

    handleCancelCreation() {
      this.showCancelModal = true
      this.trackBoardsActions('boards.CancelCreation')
    },

    handleStepperClick(step) {
      this.currentStep = step
      this.trackBoardsActions('boards.ClickStepCreationFlow', {
        step: Object.keys(this.STEPS)[step - 1],
      })
    },

    updateFormField(value, field) {
      this.board[field] = value
    },
  },
}
</script>
